import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { GoalChronologyDTO, MatchInfoDTO } from '@solverml/api';

import { LineChartGraphComponent } from '../../../graphs/view/line-chart/line-chart-graph/line-chart-graph.component';

import {
  LineChartModel,
  LineChartSeries,
  LineChartPoint
} from '../../../graphs/model/line-chart/line-chart-model';

/**
 * Componente "Dumb" (Vista) que renderiza la gráfica
 * de la cronología de goles del partido.
 */
@Component({
  selector: 'app-match-goal-chronology',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    LineChartGraphComponent
  ],
  templateUrl: './match-goal-chronology.component.html',
  styleUrls: ['./match-goal-chronology.component.scss']
})
export class MatchGoalChronologyComponent implements OnChanges {

  @Input({ required: true }) chronology!: GoalChronologyDTO | null;
  @Input({ required: true }) matchInfo!: MatchInfoDTO | null;

  /**
   * Datos transformados que se pasarán a 'app-line-graph-chart'.
   * (Tipo LineChartModel)
   */
  public transformedModel: LineChartModel | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['chronology'] || changes['matchInfo']) && this.chronology && this.matchInfo) {
      this.transformedModel = this.transformData(this.chronology, this.matchInfo);
    } else {
      this.transformedModel = null;
    }
  }

  /**
   * Transforma el GoalChronologyDTO (API) en el LineChartModel (ApexCharts)
   * que espera el componente 'app-line-graph-chart'.
   */
  private transformData(chronology: GoalChronologyDTO, info: MatchInfoDTO): LineChartModel {

    const totalMinutes = 60;
    const interval = 5; // Intervalo de 5 minutos
    const numPoints = totalMinutes / interval + 1; // 13 puntos (0, 5, ..., 60)

    // 1. (Paso Intermedio) Calcular datos minuto a minuto (61 puntos)
    const fullHomeData = new Array(totalMinutes + 1).fill(0);
    const fullAwayData = new Array(totalMinutes + 1).fill(0);

    chronology.home_team_goals.forEach(goal => {
      for (let i = goal.minute; i <= totalMinutes; i++) {
        fullHomeData[i] = goal.score;
      }
    });

    chronology.away_team_goals.forEach(goal => {
      for (let i = goal.minute; i <= totalMinutes; i++) {
        fullAwayData[i] = goal.score;
      }
    });

    // 2. (Paso Final) Muestrear (Samplear) los datos en los intervalos de 5 min
    const homePoints: LineChartPoint[] = [];
    const awayPoints: LineChartPoint[] = [];

    for (let i = 0; i < numPoints; i++) {
      const minuteIndex = i * interval; // 0, 5, 10, ...
      const labelText = minuteIndex.toString(); // '0', '5', '10'

      homePoints.push({
        label: labelText,
        value: fullHomeData[minuteIndex]
      });

      awayPoints.push({
        label: labelText,
        value: fullAwayData[minuteIndex]
      });
    }

    // 3. Construir las Series
    const homeSeries: LineChartSeries = {
      label: info.home_team_name,
      color: '#002041', // Azul oscuro (Local)
      data: homePoints
    };

    const awaySeries: LineChartSeries = {
      label: info.away_team_name,
      color: '#FF4136', // Rojo (Visitante)
      data: awayPoints
    };

    // 4. Construir el Modelo final
    return {
      title: 'Cronología de Goles',
      series: [homeSeries, awaySeries]
    };
  }
}
